/**
 * Utility functions for managing the beta experience switcher
 */

const BETA_COOKIE_NAME = 'aictrl_beta_experience_opt_in';
const BETA_COOKIE_EXPIRY_DAYS = 30;
const REDIRECT_HOST_COOKIE_NAME = 'aictrl_redirect_host';

/**
 * Sets a cookie with the given name, value, and expiration days
 */
function setCookie(name: string, value: string, days: number): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  let cookieString = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  const hostname = window.location.hostname;
  // Set domain for any multi-level domain (e.g., subdomain.example.com -> .example.com)
  if (hostname.includes('.') && !hostname.startsWith('localhost')) {
    const domainParts = hostname.split('.');
    if (domainParts.length >= 2) {
      const parentDomain = '.' + domainParts.slice(-2).join('.');
      cookieString += `;domain=${parentDomain}`;
    }
  } else if (hostname.includes("localhost")) {
    cookieString += `;domain=${hostname}`;
  }

  document.cookie = cookieString;
}

/**
 * Gets a cookie value by name
 */
function getCookie(name: string): string | null {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

/**
 * Deletes a cookie by name
 */
function deleteCookie(name: string): void {
  let cookieString = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  const hostname = window.location.hostname;
  // Set domain for any multi-level domain (e.g., subdomain.example.com -> .example.com)
  if (hostname.includes('.') && !hostname.startsWith('localhost')) {
    const domainParts = hostname.split('.');
    if (domainParts.length >= 2) {
      const parentDomain = '.' + domainParts.slice(-2).join('.');
      cookieString += `;domain=${parentDomain}`;
    }
  }
  document.cookie = cookieString;
}

/**
 * Check if user has opted into beta experience
 */
export function isBetaExperienceEnabled(): boolean {
  return getCookie(BETA_COOKIE_NAME) === 'true';
}

/**
 * Enable beta experience and set cookie
 */
export function enableBetaExperience(): void {
  setCookie(BETA_COOKIE_NAME, 'true', BETA_COOKIE_EXPIRY_DAYS);
  setCookie(REDIRECT_HOST_COOKIE_NAME, window.location.href, 399);
}

/**
 * Disable beta experience and remove cookie
 */
export function disableBetaExperience(): void {
  deleteCookie(BETA_COOKIE_NAME);
  deleteCookie(REDIRECT_HOST_COOKIE_NAME);
}

/**
 * Get the OpenWebUI URL from the provided parameter
 */
export function getOpenWebUIUrl(openwebui_url?: string): string | null {
  return openwebui_url || null;
}

/**
 * Redirect to OpenWebUI if beta experience is enabled and URL is available
 */
export function redirectToOpenWebUIIfEnabled(openwebui_url?: string): boolean {
  // Check if URL contains disable_beta=true parameter
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('disable_beta') === 'true') {
    // Remove the beta experience cookie and don't redirect
    disableBetaExperience();
    return false;
  }

  if (isBetaExperienceEnabled()) {
    const openWebUIUrl = getOpenWebUIUrl(openwebui_url);
    if (openWebUIUrl) {
      // Use replace to avoid caching and prevent back navigation issues
      window.location.replace(openWebUIUrl + "?redirected_from=" + encodeURIComponent(window.location.href));
      return true;
    }
  }
  return false;
}

/**
 * Check if OpenWebUI is available for beta experience
 */
export function isOpenWebUIAvailable(openwebui_url?: string): boolean {
  return getOpenWebUIUrl(openwebui_url) !== null;
} 